import { usePermisos } from "../../context/PermisosContext";
import TableroRecursos from "../Tableros/TableroRecursos";
// src/components/Automatizacion/Automatizacion.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AutomatizarSensores from "./AutomatizarSensores";
import AutomatizarEfectores from "./AutomatizarEfectores";

/* ===== helpers permisos ===== */


const Automatizacion = ({ initialSub, onSubChange }) => {
  const { can: hasBit } = usePermisos();
  const [searchParams, setSearchParams] = useSearchParams();

  // Gate global (la ruta ya está protegida; esto es por robustez)
  const canViewWindow = hasBit("AUTOMATIZACION_VIEW") ?? true;

  // Permisos por pestaña
  const showTablero = hasBit("AUTOMATIZACION_TAB_TABLERO_VIEW");
  const showSensores =
    (hasBit("AUTOMATIZACION_TAB_SENSORES_VIEW") ?? true) !== false;
  const showEfectores =
    (hasBit("AUTOMATIZACION_TAB_EFECTORES_VIEW") ?? true) !== false;

  // Lista visible según permisos
  const visibleTabs = useMemo(() => {
    const out = showTablero ? [{ id: "tablero", label: "Tablero de automatizaciones" }] : [];
    if (showSensores) out.push({ id: "sensores", label: "Por Sensores" });
    if (showEfectores) out.push({ id: "efectores", label: "Por Efectores" });
    return out;
  }, [showSensores, showEfectores, showTablero]);

  // --- fuente de verdad: URL ---
  const subFromUrl = searchParams.get("sub") || "";

  const computeValidTab = (candidate) => {
    if (!candidate) return visibleTabs[0]?.id || null;
    return visibleTabs.some((t) => t.id === candidate)
      ? candidate
      : visibleTabs[0]?.id || null;
  };

  // decide tab inicial: URL > prop inicial > fallback
  const initialTab = computeValidTab(subFromUrl || initialSub || "tablero");

  const [tab, setTab] = useState(initialTab);

  // Si cambian permisos/URL/prop, revalidar
  useEffect(() => {
    const next = computeValidTab(subFromUrl || initialSub || tab);
    if (next !== tab) setTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs.length, subFromUrl, initialSub]);

  // Espejo estado -> URL y notifica al padre (sin loops)
  useEffect(() => {
    if (!tab) return;
    const next = new URLSearchParams(searchParams);
    if (next.get("sub") !== tab) {
      next.set("sub", tab);
      setSearchParams(next, { replace: true });
    }
    onSubChange?.(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  if (!canViewWindow) {
    return (
      <div className="bg-neutral-900 text-white p-2 sm:p-6 min-w-0 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
        <h1 className="text-center text-2xl sm:text-3xl font-bold text-green-500 mb-4 sm:mb-6">
          GESTIÓN DE AUTOMATIZACIONES
        </h1>
        <div className="bg-neutral-800 border border-neutral-700 rounded p-4 text-center">
          <p className="text-sm text-neutral-300">
            No tienes permiso para ver esta sección.
          </p>
        </div>
      </div>
    );
  }

  if (visibleTabs.length === 0 || !tab) {
    return (
      <div className="bg-neutral-900 text-white p-2 sm:p-6 min-w-0 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
        <h1 className="text-center text-2xl sm:text-3xl font-bold text-green-500 mb-4 sm:mb-6">
          GESTIÓN DE AUTOMATIZACIONES
        </h1>
        <div className="bg-neutral-800 border border-neutral-700 rounded p-4 text-center">
          <p className="text-sm text-neutral-300">
            No hay pestañas disponibles con tus permisos actuales.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-neutral-900 text-white p-2 sm:p-6 min-w-0 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
      <h1 className="text-center text-2xl sm:text-3xl font-bold text-green-500 mb-4 sm:mb-6">
        GESTIÓN DE AUTOMATIZACIONES
      </h1>

      {/* Tabs (solo las permitidas) */}
      <div className="mb-4 sm:mb-6 flex justify-center">
        <div className="grid grid-cols-1 sm:grid-cols-3 w-full gap-2">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`w-full min-h-12 px-3 sm:px-4 py-2 rounded text-sm sm:text-base transition-all duration-200 ${
                tab === t.id
                  ? "bg-green-500 text-black"
                  : "bg-gray-700 text-white hover:bg-gray-600"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenido */}
      <div className="mt-4 sm:mt-6">
        {tab === "tablero" && showTablero && <TableroRecursos tipo="automatizaciones" onOpen={setTab} />}
        {tab === "sensores" && showSensores && <AutomatizarSensores />}
        {tab === "efectores" && showEfectores && <AutomatizarEfectores />}
      </div>
    </div>
  );
};

export default Automatizacion;
